import React, { useState, useMemo, useEffect } from "react";
import { Modal, Offcanvas, Table } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { incrementQuantity, decrementQuantity, removeFromCart } from "../redux/slices/cartSlice";
import Cart from "../compoment/icon/Cart";
import Close from "../compoment/icon/Close";
import CartEmpty from "./../assets/img/e-commerce/cart.png";
import PlaceholderImg from "./../assets/img/e-commerce/placeholder-img.png";
import MinusIcon from "../compoment/icon/MinusIcon";
import Add from "../compoment/icon/Add";
import CartConfirmationModal from "./CartConfirmationModal";
import CartRemovedModal from "./CartRemovedModal";
import { formatPrice } from "../constants";
import ProductArtworkPreview from "../compoment/features/ProductArtworkPreview";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { isMerchCartType } from "../utils/cartArtUtils";
import { getProductColorLabel, getProductSizeLabel } from "../services/productService";
import { Package, ShoppingBag } from "react-feather";
import { calculateMuseumFeeTaxTotals } from "../utils/museumFeeTax";
import { loadMuseumFeeTax } from "../redux/slices/museumSlice";

function toCartLabel(value: unknown, kind: "size" | "color" = "color"): string {
    if (value == null || value === "") return "";
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed || trimmed.toLowerCase() === "[object object]") return "";
        return trimmed;
    }
    if (kind === "size") return getProductSizeLabel(value);
    return getProductColorLabel(value);
}

type DisplayCartItem = {
    id: string;
    name: string;
    dimensions: string;
    variety?: string;
    color?: string;
    size?: string;
    finalCanvasImage?: string;
    price: number;
    imgURL?: string;
    quantity: number;
    sku_code?: string;
    productId?: string | number;
    artworkPlacement?: unknown;
    isPack?: boolean;
    type?: string;
};

type CartType = "quick" | "merch";

const CartModal = ({
    show,
    onHide,
    favouriteImg,
    variant = "offcanvas",
    onPayNow,
    onPayOnReception,
}: {
    show: boolean;
    onHide: () => void;
    favouriteImg?: any;
    variant?: "offcanvas" | "pack";
    onPayNow?: () => void | Promise<void>;
    onPayOnReception?: () => void | Promise<void>;
}) => {

    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();
    const { stationData, snapshot, selectedArt } = (location.state || {}) as {
        stationData?: { name?: string };
        snapshot?: string;
        selectedArt?: unknown;
    };
    const { items: cartItems } = useSelector((s: RootState) => s.cart || { items: [] });
    const {
        processingFeePercent,
        salesTaxPercent,
        loaded: museumLoaded,
        loading: museumLoading,
    } = useSelector((s: RootState) => s.museum);
    const [removeModal, setRemoveModal] = useState(false);
    const [removedItemModal, setRemovedItemModal] = useState(false);
    const [itemToRemove, setItemToRemove] = useState<{ id: string | number; type: string; size?: string; variety?: string; color?: string; finalCanvasImage?: string } | null>(null);

    const quickPrintCart = useMemo<DisplayCartItem[]>(() => {
        return (cartItems || [])
            .filter(item => item.type === 'onsite-printing')
            .map(item => {
                // Normalize name - ensure it's a string
                let name: string;
                if (typeof item.name === 'string') {
                    name = item.name;
                } else if (typeof item.name === 'object' && item.name !== null && 'name' in item.name) {
                    name = String((item.name as any).name || 'Product');
                } else {
                    name = String(item.name || 'Product');
                }

                // Normalize variety - ensure it's a string
                let variety: string | undefined;
                if (item.variety) {
                    if (typeof item.variety === 'string') {
                        variety = item.variety;
                    } else if (typeof item.variety === 'object' && item.variety !== null) {
                        if ('name' in item.variety) {
                            variety = String((item.variety as any).name || '');
                        } else {
                            variety = String(item.variety);
                        }
                    } else {
                        variety = String(item.variety);
                    }
                }

                return {
                    id: String(item.id),
                    name,
                    dimensions: item.size || item.inches || '4"x6"',
                    variety,
                    price: typeof item.price === 'number' ? item.price : parseFloat(String(item.price || '0')),
                    imgURL: item.imageUrl || item.imgURL || PlaceholderImg,
                    finalCanvasImage: item?.finalCanvasImage,
                    quantity: item.quantity,
                    sku_code: (item as any).sku_code || (item as any).sku,
                    productId: item.productId,
                    artworkPlacement: item.artworkPlacement,
                    isPack: item.isPack,
                    type: item.type,
                };
            });
    }, [cartItems]);

    const merchandiseCart = useMemo<DisplayCartItem[]>(() => {
        return (cartItems || [])
            .filter(item => isMerchCartType(item.type))
            .map(item => {
                // Normalize name - ensure it's a string
                let name: string;
                if (typeof item.name === 'string') {
                    name = item.name;
                } else if (typeof item.name === 'object' && item.name !== null && 'name' in item.name) {
                    name = String((item.name as any).name || 'Product');
                } else {
                    name = String(item.name || 'Product');
                }

                const sizeLabel = toCartLabel(item.size, "size") || toCartLabel(item.dimensions, "size");
                const colorLabel = toCartLabel(item.color, "color") || toCartLabel(item.variety, "color");

                return {
                    id: String(item.id),
                    name,
                    dimensions: sizeLabel,
                    variety: colorLabel || undefined,
                    color: colorLabel || undefined,
                    size: sizeLabel || undefined,
                    price: typeof item.price === 'number' ? item.price : parseFloat(String(item.price || '0')),
                    imgURL: item.imageUrl || item.imgURL || PlaceholderImg,
                    finalCanvasImage: item?.finalCanvasImage,
                    quantity: item.quantity,
                    sku_code: (item as any).sku_code || (item as any).sku,
                    productId: item.productId,
                    artworkPlacement: item.artworkPlacement,
                    isPack: item.isPack,
                    type: item.type,
                };
            });
    }, [cartItems]);

    const updateQuantity = (id: string, delta: number, type: CartType, size?: string, variety?: string, color?: string, finalCanvasImage?: string) => {
        let item;
        if (type === "quick") {
            item = (cartItems || []).find(item => String(item.id) === id && item.type === 'onsite-printing');
        } else {
            item = (cartItems || []).find(item =>
                String(item.id) === id &&
                isMerchCartType(item.type) &&
                item.size === size &&
                item.variety === variety &&
                item.color === color &&
                (typeof item.finalCanvasImage === "string" ? item.finalCanvasImage : "") === (finalCanvasImage || "")
            );
        }

        if (item) {
            if (delta > 0) {
                dispatch(incrementQuantity({
                    id: item.id,
                    type: item.type,
                    size: item.size,
                    variety: item.variety,
                    color: item.color,
                    finalCanvasImage: item.finalCanvasImage,
                }));
            } else {
                dispatch(decrementQuantity({
                    id: item.id,
                    type: item.type,
                    size: item.size,
                    variety: item.variety,
                    color: item.color,
                    finalCanvasImage: item.finalCanvasImage,
                }));
            }
        }
    };

    const removeItem = (id: string, type: CartType, size?: string, variety?: string, color?: string, finalCanvasImage?: string) => {
        let item;
        if (type === "quick") {
            item = (cartItems || []).find(item => String(item.id) === id && item.type === 'onsite-printing');
        } else {
            item = (cartItems || []).find(item =>
                String(item.id) === id &&
                isMerchCartType(item.type) &&
                item.size === size &&
                item.variety === variety &&
                item.color === color &&
                (typeof item.finalCanvasImage === "string" ? item.finalCanvasImage : "") === (finalCanvasImage || "")
            );
        }

        if (item) {
            dispatch(removeFromCart({
                id: item.id,
                type: item.type,
                size: item.size,
                variety: item.variety,
                color: item.color,
                finalCanvasImage: item.finalCanvasImage,
            }));
        }
    };

    const handleRemoveClick = (id: string, type: CartType, size?: string, variety?: string, color?: string, finalCanvasImage?: string) => {
        let item;
        if (type === "quick") {
            item = (cartItems || []).find(item => String(item.id) === id && item.type === 'onsite-printing');
        } else {
            item = (cartItems || []).find(item =>
                String(item.id) === id &&
                isMerchCartType(item.type) &&
                item.size === size &&
                item.variety === variety &&
                item.color === color &&
                (typeof item.finalCanvasImage === "string" ? item.finalCanvasImage : "") === (finalCanvasImage || "")
            );
        }

        if (item) {
            setItemToRemove({
                id: item.id,
                type: item.type,
                size: item.size,
                variety: item.variety,
                color: item.color,
                finalCanvasImage: item.finalCanvasImage,
            });
            setRemoveModal(true);
        }
    };

    const confirmRemove = () => {
        if (itemToRemove) {
            dispatch(removeFromCart(itemToRemove));
            setRemovedItemModal(true);
        }
        setRemoveModal(false);
        setItemToRemove(null);
    };

    const subtotal = useMemo(() => {
        const quickTotal = quickPrintCart.reduce((sum, item) => sum + item.price * item.quantity, 0);
        const merchTotal = merchandiseCart.reduce((sum, item) => sum + item.price * item.quantity, 0);
        return quickTotal + merchTotal;
    }, [quickPrintCart, merchandiseCart]);

    const pricing = useMemo(
        () =>
            calculateMuseumFeeTaxTotals(subtotal, {
                processingFeePercent,
                salesTaxPercent,
            }),
        [subtotal, processingFeePercent, salesTaxPercent]
    );

    const total = pricing.total;

    useEffect(() => {
        if (museumLoaded || museumLoading) return;
        dispatch(loadMuseumFeeTax());
    }, [dispatch, museumLoaded, museumLoading]);

    const packCartItems = useMemo(
        () => [
            ...quickPrintCart.map(item => ({ ...item, cartType: "quick" as CartType })),
            ...merchandiseCart.map(item => ({ ...item, cartType: "merch" as CartType })),
        ],
        [quickPrintCart, merchandiseCart]
    );

    const handleProceedToCheckout = () => {
        if (cartItems.length === 0) {
            return;
        }
        if (variant === "pack" && onPayNow) {
            void onPayNow();
            return;
        }
        onHide();
        navigate("/review-cart", {
            state: {
                stationData,
                snapshot: snapshot || favouriteImg,
                selectedArt,
            },
        });
    };

    const handlePayOnReception = () => {
        if (cartItems.length === 0 || !onPayOnReception) {
            return;
        }
        void onPayOnReception();
    };

    const renderImagePreview = (item: any) => {
        const sku = item.sku_code?.toLowerCase();
        const itemKey = item.sku_code?.toLowerCase() || `cart-${item.id}`;
        const artImage = item.finalCanvasImage || favouriteImg;
        const containerStyle = {
            width: 90,
            height: 90,
            minHeight: 90,
            overflow: 'hidden',
            position: 'relative' as const,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            objectFit: "contain" as const,
        };
        if (item.isPack) {
            return (
                <img
                    style={{ width: 50, height: 50, objectFit: "contain" }}
                    src={item.imgURL || PlaceholderImg}
                    alt={item.name}
                    className="mb-0"
                />
            );
        }

        if (artImage) {
            const imageKey = `cart-${itemKey}-${favouriteImg ? String(favouriteImg).substring(0, 50) : 'snapshot'}`;
            return (
                <div style={containerStyle}>
                    <ProductArtworkPreview
                        key={imageKey}
                        productImage={item.imgURL || PlaceholderImg}
                        artworkImage={artImage}
                        itemKey={itemKey}
                        productName={item.name}
                        skuCode={sku}
                        placement={item.artworkPlacement}
                        exportResult={false}
                    />
                </div>
            );
        }

        // Fallback to static image
        return <img style={{ width: 50, height: 50, objectFit: 'contain' }} src={item?.finalCanvasImage || item.imgURL || PlaceholderImg} alt={item.name} className="mb-0" />;
    };

    const renderCartSection = (title: string, items: DisplayCartItem[], type: CartType) => {
        return (
            <div>
                <div className="subheading mb-2">{title}</div>
                <Table className="p-3 borderless align-middle mb-0">
                    <thead>
                        <tr>
                            <th>Preview</th>
                            <th>Quantity</th>
                            <th>Frame name</th>
                            <th>Dimensions</th>
                            <th>Variety</th>
                            <th>Price</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item: any) => (

                            <tr key={`${item.id}-${item.finalCanvasImage || ""}-${item.size || ""}-${item.variety || ""}`}>
                                <td>
                                    {renderImagePreview(item)}
                                </td>
                                <td>
                                    <div className="count d-flex align-items-center">
                                        <div
                                            className={`action minus ${item.quantity > 1 ? "active" : ""}`}
                                            onClick={() => updateQuantity(item.id, -1, type, item.size, item.variety, item.color, item.finalCanvasImage)}
                                        >
                                            <MinusIcon />
                                        </div>
                                        <div className="value mx-2">{item.quantity}</div>
                                        <div
                                            className="action plus active"
                                            onClick={() => updateQuantity(item.id, 1, type, item.size, item.variety, item.color, item.finalCanvasImage)}
                                        >
                                            <Add />
                                        </div>
                                    </div>
                                </td>
                                <td style={{ fontWeight: 500 }}>{item.name}</td>
                                <td style={{ color: "#818181", textTransform: 'capitalize' }}>{item.dimensions || "--"}</td>
                                <td>{item.variety || "--"}</td>
                                <td>{formatPrice(item.price)}</td>
                                <td>
                                    <div
                                        className="removeBtn"
                                        style={{ cursor: "pointer", color: "#F80004" }}
                                        onClick={() => handleRemoveClick(item.id, type, item.size, item.variety, item.color, item.finalCanvasImage)}
                                    >
                                        Remove
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        );
    };

    return (
        <>
            {variant === "pack" ? (
                <Modal
                    className="packCartModal"
                    centered
                    show={show}
                    onHide={onHide}
                >
                    <Modal.Body>
                        <button type="button" className="packCartClose" onClick={onHide} aria-label="Close cart">
                            <Close />
                        </button>

                        <div className="packCartTitle">
                            My Cart <Cart />
                        </div>

                        {packCartItems.length > 0 ? (
                            <div className="packCartContent">
                                <div className="packCartTable">
                                    <div className="packCartRow packCartTableHeader">
                                        <span>Product name</span>
                                        <span>Quantity</span>
                                        <span>Price</span>
                                        <span>Action</span>
                                    </div>

                                    <div className="packCartRows">
                                        {packCartItems.map(item => (
                                            <div
                                                className="packCartRow"
                                                key={`${item.cartType}-${item.id}-${item.finalCanvasImage || ""}-${item.size || ""}-${item.variety || ""}`}
                                            >
                                                <span className="packCartProductName">
                                                    <span className="packCartProductTitle">
                                                        {item.name}
                                                        {item.type === "pack" && (
                                                            <span className="packCartTypeBadge" title="Pack">
                                                                <Package aria-hidden="true" />
                                                                Pack
                                                            </span>
                                                        )}
                                                        {item.type === "product" && (
                                                            <span className="packCartTypeBadge packCartTypeBadgeProduct" title="Product">
                                                                <ShoppingBag aria-hidden="true" />
                                                                Product
                                                            </span>
                                                        )}
                                                    </span>
                                                    {(() => {
                                                        const sizeLabel = toCartLabel(item.size, "size");
                                                        const colorLabel =
                                                            toCartLabel(item.color, "color") ||
                                                            toCartLabel(item.variety, "color");
                                                        const meta = [
                                                            sizeLabel ? `Size: ${sizeLabel}` : null,
                                                            colorLabel ? `Color: ${colorLabel}` : null,
                                                        ].filter(Boolean);
                                                        if (meta.length === 0) return null;
                                                        return (
                                                            <span className="packCartProductMeta">
                                                                {meta.join(" · ")}
                                                            </span>
                                                        );
                                                    })()}
                                                </span>
                                                <span>{item.quantity}</span>
                                                <span>{formatPrice(item.price * item.quantity)}</span>
                                                <button
                                                    type="button"
                                                    className="packCartRemove"
                                                    onClick={() => handleRemoveClick(
                                                        item.id,
                                                        item.cartType,
                                                        item.size,
                                                        item.variety,
                                                        item.color,
                                                        item.finalCanvasImage
                                                    )}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="packCartFooter">
                                    <div className="packCartTotals">
                                        <div className="packCartTotalsRow">
                                            <span>Subtotal</span>
                                            <span>{formatPrice(pricing.subtotal)}</span>
                                        </div>
                                        <div className="packCartTotalsRow">
                                            <span>
                                                Processing fee ({pricing.processingFeePercent}%)
                                            </span>
                                            <span>{formatPrice(pricing.processingFee)}</span>
                                        </div>
                                        <div className="packCartTotalsRow">
                                            <span>
                                                Sales tax ({pricing.salesTaxPercent}%)
                                            </span>
                                            <span>{formatPrice(pricing.salesTax)}</span>
                                        </div>
                                        <div className="packCartTotal">
                                            <span>Total:</span>
                                            <strong>{formatPrice(pricing.total)} USD</strong>
                                        </div>
                                    </div>
                                    <div className="packCartActions">
                                        <button type="button" className="packCartPay" onClick={handleProceedToCheckout}>
                                            Pay Now
                                        </button>
                                        {onPayOnReception && (
                                            <button
                                                type="button"
                                                className="packCartPayReception"
                                                onClick={handlePayOnReception}
                                            >
                                                Pay at counter
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="packCartEmpty">
                                <img src={CartEmpty} alt="" />
                                <div>Your Cart is Empty!</div>
                            </div>
                        )}
                    </Modal.Body>
                </Modal>
            ) : (
            <Offcanvas
                className="cartModalSection"
                placement="end"
                show={show}
                onHide={onHide}
            >
                <Offcanvas.Header>
                    <Offcanvas.Title className="cartHeader d-flex justify-content-between w-100">
                        <div className="title">
                            <span className="me-2">
                                <Cart />
                            </span>
                            My Cart
                        </div>
                        <div onClick={onHide} style={{ cursor: "pointer" }}>
                            <Close />
                        </div>
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="cartBody">
                    {quickPrintCart.length > 0 && renderCartSection("Quick Print", quickPrintCart, "quick")}
                    {merchandiseCart.length > 0 && renderCartSection("Merchandise Print", merchandiseCart, "merch")}
                    {quickPrintCart.length === 0 && merchandiseCart.length === 0 && (
                        <div className="text-center d-flex h-100 justify-content-center align-items-center">
                            <div>
                                <img src={CartEmpty} alt="Empty cart" style={{ width: 320, height: "auto" }} />
                                <div className="description" style={{ fontSize: 29 }}>Your Cart is Empty!</div>
                            </div>
                        </div>
                    )}
                </Offcanvas.Body>
                {cartItems.length > 0 &&
                    <div className="px-md-3">
                        <hr />
                        <div className="totalSection reviewCartFeeBreakdown">
                            <div className="reviewCartFeeRow">
                                <span>Subtotal</span>
                                <span>{formatPrice(pricing.subtotal)}</span>
                            </div>
                            <div className="reviewCartFeeRow">
                                <span>Processing fee ({pricing.processingFeePercent}%)</span>
                                <span>{formatPrice(pricing.processingFee)}</span>
                            </div>
                            <div className="reviewCartFeeRow">
                                <span>Sales tax ({pricing.salesTaxPercent}%)</span>
                                <span>{formatPrice(pricing.salesTax)}</span>
                            </div>
                            <div className="reviewCartFeeTotal">
                                <div className="total">Total</div>
                                <div className="amount">{formatPrice(total)}</div>
                            </div>
                        </div>

                        <div className="mt-2">
                            <button
                                className="btnPay mb-2"
                                onClick={handleProceedToCheckout}
                            >
                                Proceed to Checkout
                                <span className="ms-1"><svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M26.667 16L27.6098 15.0572L28.5526 16L27.6098 16.9428L26.667 16ZM6.66699 17.3333C5.93061 17.3333 5.33366 16.7364 5.33366 16C5.33366 15.2636 5.93061 14.6667 6.66699 14.6667V16V17.3333ZM18.667 8L19.6098 7.05719L27.6098 15.0572L26.667 16L25.7242 16.9428L17.7242 8.94281L18.667 8ZM26.667 16L27.6098 16.9428L19.6098 24.9428L18.667 24L17.7242 23.0572L25.7242 15.0572L26.667 16ZM26.667 16V17.3333H6.66699V16V14.6667H26.667V16Z" fill="white" />
                                </svg>
                                </span>
                            </button>
                        </div>
                    </div>
                }
            </Offcanvas>
            )}

            {removeModal && itemToRemove && (
                <CartConfirmationModal
                    show={removeModal}
                    onHide={() => setRemoveModal(false)}
                    handleDelete={confirmRemove}
                />
            )}
            {
                removedItemModal &&
                <CartRemovedModal show={removedItemModal} onHide={() => setRemovedItemModal(false)} />
            }
        </>
    );
};

export default CartModal;
